import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { platformProjectsQuery, type PlatformProject } from '@/entities/project';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { i18n } from '@/shared/i18n';
import type { SelectProjectRow } from './types';

const PAGE_SIZE = 20;

const toRow = (item: PlatformProject): SelectProjectRow => ({
  id: item.id,
  platformProjectId: item.id,
  title:
    item.name || i18n.t('projects:adaptationProjectPicker.useAdaptationProjects.untitledProject'),
  // `owner` is typed as required, but an older backend (deploy/rollout window)
  // may omit it — guard so the list never crashes on a missing field.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  ownerName: item.owner?.name ?? '—',
  createdAt: item.createdAt,
});

/**
 * Read-only project list for the picker, sourced from the platform's own list
 * (the same one Asset Planner shows): current-workspace scoped, searchable,
 * first page only.
 *
 * Deliberately no per-row view gate. The platform filters this list with the
 * same visibility rule it applies when a project is opened (workspace-visible,
 * or private with the caller a member, with the admin override), so a listed
 * row is a row Asset Planner will open; the previous gate compensated for
 * workspace-api's minutes-stale access mirror, which this source bypasses.
 * Should the platform list ever stop being caller-scoped, per-row gating via
 * getScopedPermissions('project', id) is the fallback.
 */
export function useAdaptationProjects() {
  const workspaceId = useCurrentWorkspaceId();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const query = useQuery({
    ...platformProjectsQuery(workspaceId, {
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
      // The platform sorts created_at ASC by default; without DESC a workspace
      // past the page cap would pin the oldest projects and hide new ones.
      direction: 'DESC',
    }),
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  });

  const rows = (query.data?.data ?? []).map(toRow);

  return {
    rows,
    workspaceId,
    isLoading: query.isLoading,
    isError: query.isError,
    search,
    setSearch,
    hasSearch: Boolean(debouncedSearch),
  };
}
